import React, { useState, useEffect } from 'react';
import { AlertCircle, CreditCard, Users } from 'lucide-react';

const ProfileUpgradeModal = ({ isOpen, onClose, onUpgrade, serviceRequested }) => {
  const [selectedUpgrade, setSelectedUpgrade] = useState('');
  const [loading, setLoading] = useState(false);

  // Scroll to top when modal opens
  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    if (!selectedUpgrade) return;
    
    setLoading(true);
    try {
      await onUpgrade(selectedUpgrade);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 overflow-y-auto p-2 sm:p-4 sm:pt-20">
      <div className="bg-white rounded-lg p-4 sm:p-6 md:p-8 max-w-md w-full my-4">
        <div className="flex items-center mb-4 sm:mb-6">
          <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" />
          <h3 className="text-base sm:text-lg md:text-xl font-bold break-words">Profile Upgrade Required</h3>
        </div>
        
        <div className="mb-4 sm:mb-6">
          <p className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm md:text-base break-words">
            You are currently a <strong>FREE</strong> user. To access <strong>{serviceRequested}</strong>, 
            you need to upgrade your profile and provide payment verification.
          </p>
          
          <div className="bg-blue-50 p-3 sm:p-4 rounded-lg mb-3 sm:mb-4">
            <p className="text-xs sm:text-sm text-blue-800 break-words">
              <strong>Current Service:</strong> {serviceRequested}<br/>
              <strong>Required:</strong> Payment verification & profile upgrade
            </p>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <p className="font-medium text-gray-700 mb-2 sm:mb-3 text-xs sm:text-sm md:text-base">Choose your upgrade option:</p>
          
          <div className="space-y-2 sm:space-y-3">
            <div 
              className={`border rounded-lg p-3 sm:p-4 cursor-pointer transition-colors ${
                selectedUpgrade === 'a_la_carte' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedUpgrade('a_la_carte')}
            >
              <div className="flex items-start">
                <input 
                  type="radio" 
                  checked={selectedUpgrade === 'a_la_carte'}
                  onChange={() => setSelectedUpgrade('a_la_carte')}
                  className="mr-2 sm:mr-3 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="font-medium text-sm sm:text-base">A la Carte User</span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
                    Pay per service usage • No monthly commitment
                  </div>
                </div>
              </div>
            </div>
            
            <div 
              className={`border rounded-lg p-3 sm:p-4 cursor-pointer transition-colors ${
                selectedUpgrade === 'subscriber' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedUpgrade('subscriber')}
            >
              <div className="flex items-start">
                <input 
                  type="radio" 
                  checked={selectedUpgrade === 'subscriber'}
                  onChange={() => setSelectedUpgrade('subscriber')}
                  className="mr-2 sm:mr-3 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="font-medium text-sm sm:text-base">Subscriber</span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
                    Monthly/Annual plans • 50% off premium services
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4 sm:gap-0">
          <button
            onClick={handleUpgrade}
            disabled={!selectedUpgrade || loading}
            className={`w-full sm:flex-1 py-2.5 sm:py-3 px-4 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
              selectedUpgrade && !loading
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? 'Processing...' : 'Continue with Upgrade'}
          </button>
          
          <button
            onClick={onClose}
            disabled={loading}
            className="w-full sm:flex-1 bg-gray-100 text-gray-700 py-2.5 sm:py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm sm:text-base"
          >
            Cancel
          </button>
        </div>

        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
          <p className="text-[10px] sm:text-xs text-gray-500 text-center break-words">
            By continuing, you agree to provide payment verification and upgrade your account profile.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileUpgradeModal;
